import { Box, Dialog, DialogContent, IconButton, TextField, Typography } from "@mui/material"
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { useReducer, useState, useEffect } from "react"
import CoordinatesAutocompleteReducer from "./CoordinatesAutocompleteReducer"


export default function CoordinatesAutocomplete({ opt, id, conf, updateConf, setConf }) {

    //inizializzo le location che si trovano già nella conf
    const initialLocations = conf.selectedPaginationOptions[id].list;

    //Hook useReducer per il pattern State Reducer : https://react.dev/learn/extracting-state-logic-into-a-reducer
    const [locations, dispatch] = useReducer(CoordinatesAutocompleteReducer, {list:initialLocations, conf:conf});

    const filter = createFilterOptions();
    const [openDialog, setOpenDialog] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertMsg, setAlertMsg] = useState("");

    const handleDialogOpen = () => {
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleAlertOpen = () => {
        setOpenAlert(true);
    }

    const handleAlertClose = () => {
        setOpenAlert(false);
    }

    const handleAlertMsg = (msg) => {
        setAlertMsg(msg);
    }

    //metodo per il dispatch del cambiamento della selezione
    const handleChangeCoordinatesAutocomplete = async (changedValue) => {
        await dispatch({
            type: 'changed',
            payload: {
                changedValue: changedValue,
                optId: id,
                conf: conf,
                updateConf: updateConf,
                /* setConf: setConf */
            }
        })
        setConf(locations.conf)
    }

    //metodo per il dispatch dell'aggiunta di una nuova location
    const handleAddCoordinatesAutocomplete = async (newValue) => {
        if (newValue) {
            await dispatch({
                type: 'add',
                payload: {
                    newValue: newValue,
                    optId: id,
                    conf: conf,
                    updateConf: updateConf,
                    /* setConf: setConf */
                }
            })
            const updatedConf = {...locations.conf}
            updateConf(updatedConf)
            setConf(locations.conf)
        }
    }

    //metodo per il dispatch per la rimozione di una location
    const handleDeleteCoordinatesAutocomplete = async (deletedValue) => {
        if (deletedValue) {
            await dispatch({
                type: 'delete',
                payload: {
                    deletedValue: deletedValue,
                    optId: id,
                    conf: conf,
                    updateConf: updateConf,
                    /* setConf: setConf */
                }
            })
            const updatedConf = {...locations.conf}
            updateConf(updatedConf)
            setConf(locations.conf)
        }
    }

    //imposta il valore della select se non esiste (ad esempio al primo caricamento dele opzioni)
    useEffect(() => {
        const hasNoValue = !conf.selectedPaginationOptions[id].value;
        const hasDefault = conf.selectedPaginationOptions[id].list.length > 0;

        if (hasNoValue && hasDefault) {
            handleChangeCoordinatesAutocomplete(conf.selectedPaginationOptions[id].list[0]);
        }
    }, [conf.selectedPaginationOptions[id].value, conf.selectedPaginationOptions[id].list]);

    //metodo che implementa la custom option dell'autocomplete
    const CoordinatesAutocompleteItem = ({ option }) => (
        <Stack direction='row' alignItems='center' justifyContent="space-between" width="100%">
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{option.location}</Typography>
                <Typography variant="body2" color="gray">{option.lat} | {option.long}</Typography>
            </Box>
            <IconButton disabled={opt.disableDel} aria-label="delete location" sixe="small" sx={{position: "absolute", right: "0.5em"}} onClick={(e) => {
                e.stopPropagation(); //necessario altrimenti cliccando il bottone viene selezionata anche l'opzione in automatico
                handleDeleteCoordinatesAutocomplete(option)
            }
            }
            ><DeleteIcon /></IconButton>
        </Stack>
    )

    return (
        <Stack direction='row' alignItems='center' key={opt.nome}>
            <Autocomplete
                size='small'
                //value={conf.selectedPaginationOptions[id].value ? conf.selectedPaginationOptions[id].value : (conf.selectedPaginationOptions[id].list[0] ? handleChangeCoordinatesAutocomplete(conf.selectedPaginationOptions[id].list[0]) : "")}
                value={conf.selectedPaginationOptions[id].value || null}
                onChange={(event, newValue) => {
                    handleChangeCoordinatesAutocomplete(newValue)
                }}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    return filtered;
                }}
                selectOnFocus
                clearOnBlur
                clearOnEscape
                disableClearable={true}
                handleHomeEndKeys
                id={id.toString()}
                options={locations.list}
                noOptionsText="No locations available. Add one!"
                getOptionLabel={(option) => option.location ? option.location : ""}
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={`${option.location}-${option.lat}-${option.long}`}>
                        <CoordinatesAutocompleteItem option={option} />
                    </Box>
                )}
                isOptionEqualToValue={(option, value) =>
                    option.location === value.location &&
                    option.lat === value.lat &&
                    option.long === value.long
                }
                sx={{ mt: 0, minWidth: 300, flexGrow: 1 }}
                freeSolo={false}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        label={opt.nome}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    <IconButton
                                        aria-label="add location"
                                        disabled={opt.disableAdd}
                                        color="primary"
                                        size="small"
                                        sx={{ mt: -0.5, mb: -0.5 }}
                                        onClick={handleDialogOpen}
                                    >
                                        <AddBoxIcon />
                                    </IconButton>
                                </>
                            )
                        }}
                    />
                }
            />
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event) => {
                        event.preventDefault();

                        //prendo i valori di input dell'utente
                        const formData = new FormData(event.currentTarget);
                        const formJson = Object.fromEntries(formData.entries());
                        const location = formJson.location;
                        const lat = formJson.latitude;
                        const long = formJson.longitude;

                        const newValue = {
                            "location": location,
                            "lat": lat,
                            "long": long
                        }

                        //controllo se esiste già l'esatta location inserita
                        let exist = false;
                        for (let l of conf.selectedPaginationOptions[id].list) {
                            if (l.location == newValue.location && l.lat == newValue.lat && l.long == newValue.long) {
                                exist = true;
                            }
                        }

                        if (exist) {
                            handleAlertMsg("Sorry, this location already exist.\r");
                            handleAlertOpen();
                        }
                        else {
                            handleAddCoordinatesAutocomplete(newValue);
                            handleAlertClose();
                            handleAlertMsg("");
                            handleDialogClose();
                        }
                    },
                }}
            >

                <DialogTitle>Add a new location</DialogTitle>

                <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent>

                    <DialogContentText>
                        Please insert the name of the new location and its latitude and longitude.
                    </DialogContentText>

                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="name"
                        name="location"
                        label="Location"
                        type="text"
                        fullWidth
                        variant="outlined"
                        placeholder="Padova"
                        helperText="Please enter location name"
                    />
                    <TextField
                        required
                        margin="dense"
                        id="name"
                        name="latitude"
                        label="Latitude"
                        type="text"
                        fullWidth
                        variant="outlined"
                        placeholder="45.123456"
                        helperText="It must be between -90 and 90 degrees"
                    />
                    <TextField
                        required
                        margin="dense"
                        id="name"
                        name="longitude"
                        label="Longitude"
                        type="text"
                        fullWidth
                        variant="outlined"
                        placeholder="-11.123456"
                        helperText="It must be between -180 and 180 degrees"
                    />


                    <Collapse in={openAlert}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        handleAlertClose();
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            variant="outlined"
                            severity="error"
                        >
                            {alertMsg}
                        </Alert>
                    </Collapse>

                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", mb: "1em", mt: "-1em" }}>
                    <Button type="submit" variant="contained" sx={{}}>ADD</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

